import { useEffect, useState } from "react";
import { apiSend, apiGet } from "./api";

// ---- IndexedDB (offline queue + attractions cache) ----
const DB_NAME = "safebook";
const STORE = "kv";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key, fallback) {
  try {
    const db = await openDB();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const r = tx.objectStore(STORE).get(key);
      r.onsuccess = () => resolve(r.result === undefined ? fallback : r.result);
      r.onerror = () => resolve(fallback);
    });
  } catch {
    return fallback;
  }
}

async function idbSet(key, val) {
  try {
    const db = await openDB();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(val, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    /* ignore */
  }
}

const QUEUE_KEY = "offline_queue";
const ATTR_CACHE = "attractions_cache";

function isNetworkError(e) {
  const m = String(e?.message || e);
  return e instanceof TypeError || m.includes("Failed to fetch") || m.includes("Network");
}

async function readQueue() {
  return (await idbGet(QUEUE_KEY, [])) || [];
}
async function writeQueue(q) {
  await idbSet(QUEUE_KEY, q);
}

export async function submitQueued(endpoint, payload) {
  try {
    const result = await apiSend(endpoint, "POST", payload);
    return { synced: true, result };
  } catch (e) {
    if (isNetworkError(e)) {
      const q = await readQueue();
      q.push({ localId: Math.random().toString(36).slice(2), endpoint, payload, queued_at: new Date().toISOString() });
      await writeQueue(q);
      return { synced: false };
    }
    throw e;
  }
}

export async function submitLog(payload) {
  const { synced, result } = await submitQueued("/logs", payload);
  return { synced, log: result };
}

export async function pendingCount() {
  return (await readQueue()).length;
}

let syncing = false;
export async function syncPending() {
  if (syncing) return 0;
  syncing = true;
  let synced = 0;
  try {
    const q = await readQueue();
    if (q.length === 0) return 0;
    const remaining = [];
    for (const item of q) {
      try {
        await apiSend(item.endpoint || "/logs", "POST", item.payload);
        synced++;
      } catch (e) {
        if (isNetworkError(e)) remaining.push(item);
      }
    }
    await writeQueue(remaining);
  } finally {
    syncing = false;
  }
  return synced;
}

export async function getAttractionsCached() {
  try {
    const list = await apiGet("/attractions");
    await idbSet(ATTR_CACHE, list);
    return list;
  } catch {
    return (await idbGet(ATTR_CACHE, [])) || [];
  }
}

export function useIsOnline() {
  const [online, setOnline] = useState(navigator.onLine !== false);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}
